import React from 'react';
import type { ReactElement } from 'react';

import styled from 'styled-components';
import type { DefaultTheme } from 'styled-components';

import { LightText } from 'apps/promo/components/common/fromAuth/LightText/LightText';
import { PromoText } from 'apps/promo/components/common/promo-text/PromoText';
import { Text } from 'apps/promo/components/common/Text/Text';
import type { TextColor, TextVariant } from 'apps/promo/components/common/Text/Text';
import promoColors from 'apps/promo/theme/promoColors';

import type { Language } from '@/types/globals';

type MessageArgs = Array<string | number | undefined> | string | number | Record<string, unknown> | null | undefined;

type DateInput = string | number | Date;

type HighlightComponent = 'text' | 'lightText' | 'promoText';

type HighlightStyle = {
    basicColor?: TextColor;
    lightTextColor?: 'primary' | 'secondary';
    variant?: TextVariant | null;
    ellipsis?: boolean | null;
    smallCaps?: boolean | null;
};

type TimerId = ReturnType<typeof setTimeout>;

export const resolveArguments = (message = '', args?: MessageArgs): string => {
    const regex = /\{\d}/g;
    const safeMessage = message ?? '';
    if (Array.isArray(args)) {
        let resolveMessage = safeMessage;
        const matchArray = safeMessage.match(regex);
        if (matchArray) {
            args.forEach((argument, index) => {
                resolveMessage = resolveMessage.replaceAll(`\{${index}\}`, String(argument ?? ''));
            });
            if (matchArray.length !== args.length && !__isDev__) {
                resolveMessage = resolveMessage.replace(regex, '');
            }
        }
        return resolveMessage;
    }
    if (isObject(args)) {
        const matchArray = safeMessage.match(regex);
        if (matchArray && matchArray[0]) {
            // eslint-disable-next-line @typescript-eslint/no-base-to-string
            const newMessForFirst = safeMessage.replaceAll(`\{0\}`, String(args ?? ''));
            if (matchArray.length > 1 && !__isDev__) {
                return newMessForFirst.replace(regex, '');
            }
            return newMessForFirst;
        }
        return safeMessage;
    }
    return safeMessage;
};

type DynamicCountriesAmounts = Record<string, Record<string, string>>;

export const resolveDynamicAmounts = (text = '', amounts?: DynamicCountriesAmounts | null, country?: string | null): string => {
    if (!text || !amounts) {
        return text;
    }
    const countryAmounts = (country && amounts[country.toLowerCase()]) || amounts.default;
    if (!countryAmounts) {
        return text;
    }
    return text.replace(/\{(\w+)}/g, (match: string, key: string) => (key in countryAmounts ? countryAmounts[key] : match));
};

export const getCurrentLang = (languages: Language[] = [], locale = ''): string => {
    const lang = languages.find((el) => el.localeCode === locale);
    return lang ? lang.name : '';
};

export const getYYYYMMDDDate = (date: DateInput): string => {
    const jsDate = new Date(date);
    return `${jsDate.getFullYear()}-${addZero(jsDate.getMonth() + 1)}-${addZero(jsDate.getDate())}`;
};

export const getDDMMMYYYYDate = (date: DateInput, lang: string): string => {
    const jsDate = new Date(date);
    return `${addZero(jsDate.getDate())} ${jsDate.toLocaleString(lang, { month: 'short' })} ${addZero(jsDate.getFullYear())}`;
};

export const addZero = (value: number): string => (value < 10 ? `0${value}` : String(value));

export const timer = (timerID: TimerId | null, callFun: () => void, delay = 300): TimerId => {
    if (timerID) {
        clearTimeout(timerID);
    }
    const newTimer = setTimeout(() => {
        callFun();
    }, delay);
    return newTimer;
};

export const isObject = (obj: unknown): obj is Record<string, unknown> | Array<unknown> | string | number => {
    return obj !== null && typeof obj !== 'undefined';
};

export const random128Hex = (): string => {
    const random16Hex = () => {
        return (0x10000 | (Math.random() * 0x10000)).toString(16).substr(1);
    };
    return (
        random16Hex() + random16Hex() + '-' + random16Hex() + '-' + random16Hex() + '-' + random16Hex() + '-' + random16Hex() + random16Hex() + random16Hex()
    );
};

export const getLangCountryPrefix = (lang?: string | null, country?: string | null): string => {
    let prefix = '/';
    if (lang && typeof country === 'string') {
        prefix = `/${lang}-${country.toLowerCase()}`;
    } else if (lang) {
        prefix = `/${lang}`;
    }
    return prefix;
};

export const getExtra = (extra: string | null | undefined, extraName: string): string | null => {
    if (extra && typeof extra === 'string') {
        const extras = extra.split(';');
        const filtered = extras.filter((e) => e && e.split('=')[0] === extraName);
        return filtered && filtered[0] ? filtered[0].split('=')[1] : null;
    }
    return null;
};

export const toLowerCase = (str: string | null | undefined): string => {
    return str && typeof str === 'string' ? str.toLowerCase() : '';
};

export const toUpperCase = (str: string | null | undefined): string => {
    return str && typeof str === 'string' ? str.toUpperCase() : '';
};

const softBlockErrors = [
    160, 611, 612, 613, 615, 616, 617, 618, 619, 620, 621, 622, 631, 632, 634, 635, 636, 637, 638, 639, 640, 641, 642, 644, 649, 656, 6001, 6002, 6003, 6005,
    6006, 6007, 6008, 6010, 6011, 6012, 6013, 6014, 6015, 6016, 6017, 6018, 6019, 6020, 6021, 6022, 6023, 6024, 6025, 6026, 6027, 6028, 6029, 6030, 6031, 6032,
    6033, 6034, 6035, 6036, 6037, 6038, 6039, 6040, 6041, 6042, 6043, 6044, 6045, 6046, 6047, 6048, 6049, 6050, 6051, 6052, 6054, 6055, 6056, 6057, 6063, 6064,
    6065,
];
const hardBlockErrors = [150, 153, 655, 6059, 6060, 6061, 6062, 6099];

export const isSoftOrHardServerError = (error?: number | null): boolean => {
    if (typeof error !== 'number') {
        return false;
    }
    return softBlockErrors.includes(error) || hardBlockErrors.includes(error);
};

const escapeRegExp = (value: string): string => {
    return value.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
};

const getThemePurple = (theme: DefaultTheme): string => {
    const typedTheme = theme as DefaultTheme & { purple_100?: string };
    return typedTheme.purple_100 ?? promoColors.primary;
};

const Highlight = styled.span<{ useThemeColor?: boolean; isPromo?: boolean }>`
    color: ${(props) => {
        const themeColor = getThemePurple(props.theme);
        return props.useThemeColor || !props.isPromo ? themeColor : promoColors.primary;
    }};
`;

export const getHighlightedText = (
    query: string,
    text: string | null | undefined,
    componentType: HighlightComponent = 'text',
    style: HighlightStyle = {},
    useThemeColor = false,
): ReactElement => {
    const escapedQuery = escapeRegExp(query);
    const safeText = text ?? '';
    const parts = safeText.split(new RegExp(`(${escapedQuery})`, 'gi'));
    const isPromo = componentType === 'lightText' || componentType === 'promoText';
    const textVariant = style.variant ?? undefined;
    const textProps = {
        variant: textVariant,
        ellipsis: style.ellipsis ?? undefined,
        smallCaps: style.smallCaps ?? undefined,
    };
    const highlightedParts = parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
            <Highlight key={`${safeText}-${i}`} isPromo={isPromo} useThemeColor={useThemeColor}>
                {part}
            </Highlight>
        ) : (
            <React.Fragment key={`${safeText}-${i}`}>{part}</React.Fragment>
        ),
    );

    if (componentType === 'lightText') {
        return (
            <LightText {...textProps} color={style.lightTextColor ?? 'primary'}>
                {highlightedParts}
            </LightText>
        );
    }

    const TextComponent = componentType === 'promoText' ? PromoText : Text;

    return (
        <TextComponent {...textProps} color={style.basicColor ?? 'darkGray'} {...(componentType === 'promoText' ? { useThemeColor } : {})}>
            {highlightedParts}
        </TextComponent>
    );
};
